import type { ReactNode } from "react";
import { Inbox } from "lucide-react";

import { cn } from "@/lib/utils/cn";

import { EmptyState, ErrorState, NoAccessState, Skeleton, SkeletonTableRow } from "./feedback";

export type QueryStateStatus = "loading" | "empty" | "error" | "no-access" | "success";
export type QueryStateProps = {
  status: QueryStateStatus; children?: ReactNode; title?: string; description?: string; action?: ReactNode;
  loadingLabel?: string; loadingRows?: number; loadingVariant?: "page" | "list"; className?: string;
};

/** One accessible presentation for async loading, empty, error, and permission states. */
export function QueryState({ status, children, title, description, action, loadingLabel = "Loading content", loadingRows = 5, loadingVariant = "page", className }: QueryStateProps) {
  if (status === "success") return <>{children}</>;
  if (status === "loading") return <div className={cn("rounded-card border border-card-border bg-surface-primary p-[var(--density-panel-padding)]", className)} role="status" aria-label={loadingLabel} aria-live="polite">
    <span className="sr-only">{loadingLabel}</span>
    {loadingVariant === "list"
      ? <div>{Array.from({ length: loadingRows }).map((_, index) => <SkeletonTableRow key={index} />)}</div>
      : <div className="space-y-ds-4"><Skeleton className="h-5 w-48" /><Skeleton className="h-4 w-full max-w-2xl" /><Skeleton className="h-32 w-full" /></div>}
  </div>;
  if (status === "error") return <ErrorState title={title ?? "Something went wrong"} description={description ?? "The requested content could not be loaded."} action={action} className={className} />;
  if (status === "no-access") return <NoAccessState title={title ?? "Access unavailable"} description={description ?? "You do not have permission to view this content."} action={action} className={className} />;
  return <EmptyState icon={Inbox} title={title ?? "Nothing here yet"} description={description ?? "No matching content is available."} action={action} className={className} />;
}

export type QueryStateBoundaryProps = Omit<QueryStateProps, "status"> & { isLoading?: boolean; error?: unknown; isEmpty?: boolean; noAccess?: boolean };
export function QueryStateBoundary({ isLoading, error, isEmpty, noAccess, children, ...props }: QueryStateBoundaryProps) {
  const status: QueryStateStatus = isLoading ? "loading" : noAccess ? "no-access" : error ? "error" : isEmpty ? "empty" : "success";
  return <QueryState status={status} {...props}>{children}</QueryState>;
}
